/**
 * Namespaced console logger for diagnostic logs, disabled by default.
 *
 * Enable by setting the debug key in localStorage. Supports wildcards
 *
 * Silenced logs are recorded as Sentry breadcrumbs so the latest diagnostic
 * logs show up on error reports.
 */
import * as Sentry from "@sentry/core";
// localStorage.getItem is a synchronous (potentially cross-process) API, so
// don't hit it on every log call from hot paths; cache the parsed patterns
// briefly instead. Runtime toggling still works, with up to 1s of latency.
const PATTERNS_CACHE_TTL = 1_000; // ms
let cachedPatterns = [];
let patternsReadAt = Number.NEGATIVE_INFINITY;
/** @internal Test-only: force the next call to re-read localStorage */
export const _resetDebugLogPatternsCache = () => {
    patternsReadAt = Number.NEGATIVE_INFINITY;
};
const getPatterns = () => {
    const now = Date.now();
    if (now - patternsReadAt >= PATTERNS_CACHE_TTL) {
        patternsReadAt = now;
        try {
            // Guard for non-browser environments (node tests)
            cachedPatterns =
                typeof localStorage === "undefined"
                    ? []
                    : (localStorage.getItem("debug") ?? "")
                        .split(",")
                        .map((pattern) => pattern.trim())
                        .filter(Boolean);
        }
        catch {
            cachedPatterns = [];
        }
    }
    return cachedPatterns;
};
const matches = (pattern, namespace) => {
    if (pattern === "*")
        return true;
    if (pattern.endsWith("*"))
        return namespace.startsWith(pattern.slice(0, -1));
    return pattern === namespace;
};
const isEnabled = (namespace) => getPatterns().some((pattern) => matches(pattern, namespace));
// This is safe : sentry use a queue for breadcrums with max_size 100
export const createDebugLogger = (namespace) => ({
    enabled: () => isEnabled(namespace),
    info: (message, ...args) => {
        if (isEnabled(namespace)) {
            console.info(`[${namespace}]`, message, ...args);
        }
        else {
            Sentry.addBreadcrumb({
                category: namespace,
                message,
                data: extractArgsData(args),
                level: "info",
            });
        }
    },
    error: (message, ...args) => {
        if (isEnabled(namespace)) {
            console.error(`[${namespace}]`, message, ...args);
        }
        else {
            Sentry.addBreadcrumb({
                category: namespace,
                message,
                data: extractArgsData(args),
                level: "error",
            });
        }
    },
});
function extractArgsData(args) {
    return args.length > 0
        ? args.length === 1 && typeof args[0] === "object" && args[0] !== null
            ? args[0]
            : { args }
        : undefined;
}
//# sourceMappingURL=debugLog.js.map